import { useEffect, useRef, useState } from "react";
import AkyoQuestion, {
  type NativeQuestion,
  type Fragment,
} from "./AkyoQuestion";
import "./training.css";
type Chapter = {
  id: string;
  label: string;
  skills: { id: string; label: string }[];
};
type Attempt = {
  id: string;
  studentName?: string;
  studentUserId?: string;
  chapterLabels?: string[];
  total: number;
  answered: number;
  correct: number;
  score20: number | null;
  createdAt: string;
  finishedAt: string | null;
  expiresAt?: string | null;
};
type Review = {
  question: NativeQuestion;
  answer: unknown;
  correct: boolean;
  correction: { answer: Record<string, any>; explanation: Fragment };
};
type Detail = Attempt & { question: NativeQuestion | null; review: Review[] };
async function api<T>(
  path: string,
  body?: unknown,
  method = "POST",
): Promise<T> {
  const r = await fetch("/api/qcm/training" + path, {
    credentials: "include",
    ...(body === undefined
      ? {}
      : {
          method,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }),
  });
  const data = await r.json();
  if (!r.ok) throw Error(data.error || "Entraînement indisponible");
  return data;
}
function History({
  attempts,
  onOpen,
}: {
  attempts: Attempt[];
  onOpen?: (id: string) => void;
}) {
  return (
    <div className="training-history">
      <h2>Activité d’entraînement</h2>
      <p>
        {attempts.length} tentative(s) affichée(s),{" "}
        {attempts.filter((a) => a.finishedAt).length} terminée(s),{" "}
        {attempts.reduce((n, a) => n + a.answered, 0)} réponse(s) validée(s).
      </p>
      <p>
        Notes d’entraînement, séparées des évaluations du professeur. Les
        tentatives inachevées n’ont pas de note finale.
      </p>
      {!attempts.length ? (
        <p>Aucune tentative.</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Élève / tentative</th>
              <th>Chapitres</th>
              <th>Réponses</th>
              <th>Justes</th>
              <th>Note</th>
            </tr>
          </thead>
          <tbody>
            {attempts.map((a) => (
              <tr key={a.id}>
                <td>{new Date(a.createdAt).toLocaleString("fr-FR")}</td>
                <td>
                  {a.studentName || "Mon entraînement"}{" "}
                  {onOpen && (
                    <button onClick={() => onOpen(a.id)}>
                      {a.finishedAt ? "Revoir" : "Reprendre"}
                    </button>
                  )}
                </td>
                <td>{a.chapterLabels?.join(", ")}</td>
                <td>
                  {a.answered} / {a.total}
                </td>
                <td>{a.correct}</td>
                <td>{a.score20 === null ? "En cours" : `${a.score20} / 20`}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
export default function TrainingApp({
  teacher = false,
}: {
  teacher?: boolean;
}) {
  const [chapters, setChapters] = useState<Chapter[]>([]),
    [ids, setIds] = useState<string[]>([]),
    [count, setCount] = useState(10);
  const [attempts, setAttempts] = useState<Attempt[]>([]),
    [detail, setDetail] = useState<Detail | null>(null),
    [reviewIndex, setReviewIndex] = useState<number | null>(null);
  const [loadedClass, setLoadedClass] = useState("");
  const [classes, setClasses] = useState<{ id: string; name: string }[]>([]),
    [classId, setClassId] = useState("");
  const [error, setError] = useState(""),
    [notice, setNotice] = useState(""),
    [busy, setBusy] = useState(false),
    [loading, setLoading] = useState(true);
  const requestId = useRef<string | null>(null),
    inFlight = useRef(false);
  async function context() {
    const d = await api<{ chapters: Chapter[]; attempts: Attempt[] }>(
      "/context",
    );
    setChapters(d.chapters);
    setAttempts(d.attempts);
    setIds((v) => v.filter((id) => d.chapters.some((c) => c.id === id)));
  }
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        if (teacher) {
          const d = await api<{ classes: { id: string; name: string }[] }>(
            "/admin/classes",
          );
          if (!active) return;
          setClasses(d.classes);
          setClassId(d.classes[0]?.id || "");
          const r = await fetch("/api/qcm/akyo/reference", {
            credentials: "include",
          });
          const b = await r.json();
          if (!r.ok) throw Error(b.error);
          if (active) setChapters(b.chapters);
        } else {
          await context();
          const id = new URLSearchParams(location.search).get("attempt");
          if (id) {
            const d = await api<Detail>("/attempts/" + encodeURIComponent(id));
            if (active) {
              setDetail(d);
              setReviewIndex(d.review.length ? d.review.length - 1 : null);
            }
          }
        }
      } catch (e) {
        if (active) setError((e as Error).message);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [teacher]);
  useEffect(() => {
    if (!teacher || !classId) return;
    let active = true;
    setLoading(true);
    setLoadedClass("");
    setIds([]);
    setAttempts([]);
    setNotice("");
    setError("");
    api<{ chapterIds: string[]; attempts: Attempt[] }>(
      "/admin/classes/" + encodeURIComponent(classId),
    )
      .then((d) => {
        if (active) {
          setIds(d.chapterIds);
          setAttempts(d.attempts);
          setLoadedClass(classId);
        }
      })
      .catch((e) => {
        if (active) setError(e.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [classId, teacher]);
  async function run(fn: () => Promise<void>, propagate = false) {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await fn();
    } catch (e) {
      setError((e as Error).message);
      if (propagate) throw e;
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }
  function show(d: Detail) {
    setDetail(d);
    setReviewIndex(d.review.length ? d.review.length - 1 : null);
    history.replaceState(null, "", "?attempt=" + encodeURIComponent(d.id));
  }
  const review =
    detail && reviewIndex !== null ? detail.review[reviewIndex] : null;
  return (
    <main className="training-page">
      <a href={teacher ? "/qcm-admin" : "/"}>
        ← {teacher ? "QCM professeur" : "Mon espace"}
      </a>
      <h1>
        {teacher ? "Entraînement : autorisations et suivi" : "Entraînement QCM"}
      </h1>
      {error && <p role="alert">{error}</p>}
      {notice && <p role="status">{notice}</p>}
      {loading && <p role="status">Chargement…</p>}
      {teacher && (
        <label>
          Classe{" "}
          <select
            value={classId}
            disabled={busy}
            onChange={(e) => setClassId(e.target.value)}
          >
            {classes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
      )}
      {teacher && !classes.length && !loading && (
        <p>Aucune classe disponible.</p>
      )}
      {!detail && (
        <>
          <fieldset
            disabled={
              busy ||
              loading ||
              (teacher && (!classId || loadedClass !== classId))
            }
          >
            <legend>
              {teacher
                ? "Chapitres autorisés pour cette classe"
                : "Choisis tes chapitres"}
            </legend>
            {!chapters.length && !loading && (
              <p>Aucun chapitre autorisé disponible.</p>
            )}
            {chapters.map((c) => (
              <label className="training-chapter" key={c.id}>
                <input
                  type="checkbox"
                  checked={ids.includes(c.id)}
                  onChange={(e) => {
                    requestId.current = null;
                    setIds((v) =>
                      e.target.checked
                        ? [...v, c.id]
                        : v.filter((x) => x !== c.id),
                    );
                  }}
                />
                {c.label}
              </label>
            ))}
            {teacher ? (
              <>
                <p>
                  Décocher un chapitre empêche les nouveaux entraînements. Les
                  tentatives commencées et leurs résultats restent disponibles.
                </p>
                <button
                  onClick={() =>
                    void run(async () => {
                      await api(
                        "/admin/classes/" + encodeURIComponent(classId),
                        { chapterIds: ids },
                        "PUT",
                      );
                      setNotice("Autorisations enregistrées.");
                    })
                  }
                >
                  Enregistrer les autorisations
                </button>
              </>
            ) : (
              <>
                <label>
                  Nombre de questions{" "}
                  <input
                    type="number"
                    min={1}
                    max={20}
                    value={count}
                    onChange={(e) => {
                      requestId.current = null;
                      setCount(Number(e.target.value));
                    }}
                  />
                </label>
                <button
                  disabled={
                    !ids.length ||
                    !Number.isInteger(count) ||
                    count < 1 ||
                    count > 20
                  }
                  onClick={() =>
                    void run(async () => {
                      requestId.current ||= crypto.randomUUID();
                      const d = await api<Detail>("/attempts", {
                        chapterIds: ids,
                        count,
                        requestId: requestId.current,
                      });
                      requestId.current = null;
                      show(d);
                    })
                  }
                >
                  Commencer
                </button>
              </>
            )}
          </fieldset>
        </>
      )}
      {detail && (
        <section>
          {detail.expiresAt && !detail.finishedAt && (
            <p>
              Reprise possible jusqu’au{" "}
              {new Date(detail.expiresAt).toLocaleString("fr-FR")} (durée du
              tirage Akyo). Tes réponses validées restent conservées.
            </p>
          )}
          <p>
            {detail.answered} réponse(s) validée(s) sur {detail.total}
          </p>
          {review ? (
            <>
              <h2>Correction — question {(reviewIndex ?? 0) + 1}</h2>
              <p>{review.correct ? "Bonne réponse" : "Réponse incorrecte"}</p>
              <AkyoQuestion
                key={review.question.instanceId + "review"}
                question={review.question}
                response={review.answer}
                correction={review.correction}
                disabled
              />
              {!detail.finishedAt && (
                <button disabled={busy} onClick={() => setReviewIndex(null)}>
                  Question suivante
                </button>
              )}
            </>
          ) : (
            detail.question && (
              <>
                <h2>
                  Question {detail.answered + 1} / {detail.total}
                </h2>
                <AkyoQuestion
                  key={detail.id + detail.question.instanceId}
                  question={detail.question}
                  disabled={busy}
                  onAnswer={(answer) =>
                    run(async () => {
                      const next = await api<Detail>(
                        "/attempts/" + detail.id + "/answer",
                        { questionId: detail.question!.instanceId, answer },
                      );
                      show(next);
                    }, true)
                  }
                />
              </>
            )
          )}
          {detail.finishedAt && (
            <>
              <h2>Entraînement terminé : {detail.score20} / 20</h2>
              <p>
                {detail.correct} bonne(s) réponse(s) sur {detail.total}.
              </p>
              <nav aria-label="Corrections">
                {detail.review.map((r, i) => (
                  <button
                    key={r.question.instanceId}
                    onClick={() => setReviewIndex(i)}
                  >
                    Question {i + 1}
                  </button>
                ))}
              </nav>
            </>
          )}
          <button
            disabled={busy}
            onClick={() =>
              void run(async () => {
                await context();
                setDetail(null);
                setReviewIndex(null);
                requestId.current = null;
                history.replaceState(null, "", location.pathname);
              })
            }
          >
            {detail.finishedAt
              ? "Nouvel entraînement"
              : "Retour aux entraînements (progression conservée)"}
          </button>
        </section>
      )}
      {!detail && (
        <>
          <History
            attempts={attempts}
            onOpen={
              teacher
                ? undefined
                : (id) =>
                    void run(async () =>
                      show(await api<Detail>("/attempts/" + id)),
                    )
            }
          />
          {teacher && (
            <>
              <p>
                Les 500 tentatives les plus récentes, par date de lancement.
                Compare les notes successives pour suivre l’évolution.
              </p>
              <button
                disabled={busy || loading || !classId}
                onClick={() =>
                  void run(async () => {
                    const d = await api<{ attempts: Attempt[] }>(
                      "/admin/classes/" + encodeURIComponent(classId),
                    );
                    setAttempts(d.attempts);
                  })
                }
              >
                Actualiser le suivi
              </button>
            </>
          )}
        </>
      )}
    </main>
  );
}
